import React, { useState } from 'react';
import { useGetQuotes, useReplyToQuote } from '../../services/quote.service';
import { AdminSidebarLayout } from '../../components/AdminSidebar';
import { Search, Mail, User, Calendar, MessageSquare, Clock, CheckCircle, ChevronRight, X, ExternalLink, FileIcon, Loader2 } from 'lucide-react';

const ProjectRequests = () => {
  const { data: quotes, isLoading, refetch } = useGetQuotes();
  const { mutate: sendReply, isLoading: isReplying } = useReplyToQuote();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [replyContent, setReplyContent] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const filteredQuotes = quotes?.filter(quote => 
    quote.fullName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quote.projectName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    quote.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleOpenReplyModal = (quote) => {
    setSelectedQuote(quote);
    setReplyContent(quote.replyContent || '');
    setIsModalOpen(true);
    setSuccessMessage('');
  };

  const handleSendReply = (e) => {
    e.preventDefault();
    if (!replyContent.trim()) return;

    sendReply({ 
      quoteId: selectedQuote._id, 
      replyContent 
    }, {
      onSuccess: () => {
        setSuccessMessage('Reply sent successfully!');
        refetch();
        setTimeout(() => {
          setIsModalOpen(false);
          setSelectedQuote(null);
        }, 1500);
      }
    });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <AdminSidebarLayout currentPath="/project-requests">
      <div className="p-4 md:p-8 max-w-7xl mx-auto dark:bg-[#121212] min-h-screen transition-colors duration-200">
        <header className="mb-8">
          <h1 className="text-lg md:text-2xl font-medium md:font-bold text-[#174568] dark:text-blue-400">Project Requests</h1>
          <p className="text-gray-500 dark:text-gray-400 text-[12px] md:text-base">Manage and respond to custom project submissions from the platform.</p>
        </header>

        {/* Search and Stats */}
        <div className="mb-6 flex flex-col md:flex-row gap-4 items-center justify-between">
          <div className="relative w-full md:w-96">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by name, email or project..."
              className="w-full pl-10 pr-4 py-2 text-[12px] md:text-base border border-gray-200 dark:border-[#32353c] dark:bg-inherit dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#174568]/20 dark:focus:ring-blue-500/20 focus:border-[#174568] dark:focus:border-blue-500 transition-all"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-4">
            <div className="bg-white dark:bg-[#1d2026] px-4 py-2 rounded-lg shadow-sm border border-gray-100 dark:border-[#32353c]">
               <span className="text-sm text-gray-500 dark:text-gray-400">Total: </span>
               <span className="font-bold text-[#174568] dark:text-blue-400">{quotes?.length || 0}</span>
            </div>
            <div className="bg-white dark:bg-[#1d2026] px-4 py-2 rounded-lg shadow-sm border border-gray-100 dark:border-[#32353c]">
               <span className="text-sm text-gray-500 dark:text-gray-400">Open: </span>
               <span className="font-bold text-orange-500">{quotes?.filter(q => q.status === 'open').length || 0}</span>
            </div>
          </div>
        </div>

        {/* Requests Table/List */}
        <div className="bg-white dark:bg-[#141414] rounded-xl shadow-sm border border-gray-100 dark:border-gray-800 overflow-hidden transition-colors">
          {isLoading ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400 flex items-center justify-center">
              <Loader2 className="animate-spin" size={24} />
            </div>
          ) : filteredQuotes?.length === 0 ? (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400">No project requests found.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 dark:bg-[#1d2026]/60 border-b border-gray-100 dark:border-[#32353c] text-gray-600 dark:text-gray-400 text-sm">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Submitter</th>
                    <th className="px-6 py-4 font-semibold">Project</th>
                    <th className="px-6 py-4 font-semibold">Date</th>
                    <th className="px-6 py-4 font-semibold">Status</th>
                    <th className="px-6 py-4 font-semibold text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50 dark:divide-gray-800">
                  {filteredQuotes.map((quote) => (
                    <tr key={quote._id} className="hover:bg-gray-50/50 dark:hover:bg-gray-800/50 transition-colors group">
                      <td className="px-6 py-4">
                        <div className="flex flex-col">
                          <span className="text-[12px] md:text-base font-medium text-gray-900 dark:text-white">{quote.fullName}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400">{quote.email}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col max-w-xs md:max-w-md">
                          <span className="text-[12px] md:text-base font-medium text-gray-800 dark:text-gray-200 truncate">{quote.projectName}</span>
                          <span className="text-xs text-gray-500 dark:text-gray-400 truncate">{quote.projectDescription}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(quote.createdAt)}
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          quote.status === 'open' 
                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400' 
                            : 'bg-green-100 text-green-700 dark:bg-green-500/20 dark:text-green-400'
                        }`}>
                          {quote.status === 'open' ? 'Waiting Response' : 'Replied'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button 
                          onClick={() => handleOpenReplyModal(quote)}
                          className={`text-[#174568] dark:text-blue-400 hover:bg-[#174568] dark:hover:bg-blue-600 hover:text-white p-2 rounded-lg transition-all duration-200 border border-[#174568]/10 dark:border-blue-400/20`}
                        >
                          {quote.status === 'open' ? 'Reply' : 'View Response'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Reply Modal */}
        {isModalOpen && selectedQuote && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => !isReplying && setIsModalOpen(false)}></div>
            <div className="relative bg-white dark:bg-[#0b0e14] rounded-2xl shadow-2xl w-full max-w-2xl overflow-hidden animate-in fade-in zoom-in duration-200 border border-gray-200 dark:border-gray-800">
              <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-gray-800">
                <div>
                  <h3 className="text-xl font-bold text-[#174568] dark:text-blue-400">
                    {selectedQuote.status === 'open' ? 'Respond to Request' : 'Review Response'}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Project: {selectedQuote.projectName}</p>
                </div>
                <button 
                  onClick={() => setIsModalOpen(false)}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                >
                  <X size={20} className="text-gray-400" />
                </button>
              </div>

              <div className="p-6 max-h-[80vh] overflow-y-auto">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-gray-50 dark:bg-[#1d2026]/50 p-4 rounded-xl border border-gray-100 dark:border-[#32353c]">
                     <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                        <User size={12} /> SUBMITTER
                     </div>
                     <p className="font-semibold text-gray-800 dark:text-white">{selectedQuote.fullName}</p>
                     <p className="text-sm text-gray-500 dark:text-gray-400">{selectedQuote.email}</p>
                  </div>
                  <div className="bg-gray-50 dark:bg-[#1d2026]/50 p-4 rounded-xl border border-gray-100 dark:border-[#32353c]">
                     <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                        <Clock size={12} /> SUBMITTED ON
                     </div>
                     <p className="font-semibold text-gray-800 dark:text-white">{formatDate(selectedQuote.createdAt)}</p>
                  </div>
                </div>

                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Original Request:</h4>
                  <div className="bg-blue-50/50 dark:bg-blue-900/10 p-4 rounded-xl border border-blue-100/50 dark:border-blue-900/20 italic text-gray-700 dark:text-gray-300 text-sm leading-relaxed whitespace-pre-wrap">
                    {selectedQuote.projectDescription}
                  </div>
                  {selectedQuote.skills?.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                       {selectedQuote.skills.map((skill, i) => (
                         <span key={i} className="px-2 py-0.5 bg-gray-100 dark:bg-[#1d2026] text-gray-600 dark:text-gray-300 rounded text-[10px] uppercase font-bold border border-gray-200 dark:border-[#32353c]">
                           {skill}
                         </span>
                       ))}
                    </div>
                  )}
                </div>

                {selectedQuote.files?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Attachments ({selectedQuote.files.length}):</h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {selectedQuote.files.map((fileUrl, index) => {
                        const isImage = /\.(jpg|jpeg|png|webp|avif|gif)$/i.test(fileUrl);
                        return (
                          <a 
                            key={index}
                            href={fileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group relative border border-gray-100 rounded-xl overflow-hidden hover:ring-2 hover:ring-[#174568] transition-all"
                          >
                            <div className="aspect-square bg-gray-50 flex items-center justify-center">
                              {isImage ? (
                                <img src={fileUrl} alt={`Attachment ${index + 1}`} className="w-full h-full object-cover" />
                              ) : (
                                <div className="flex flex-col items-center gap-1 text-gray-400">
                                  <FileIcon size={24} />
                                  <span className="text-[10px] font-medium uppercase">File</span>
                                </div>
                              )}
                            </div>
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                              <ExternalLink size={18} className="text-white" />
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}

                <form onSubmit={handleSendReply}>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                    {selectedQuote.status === 'open' ? 'Your Response:' : 'Admin Response:'}
                  </h4>
                  <textarea
                    readOnly={selectedQuote.status === 'replied'}
                    value={replyContent}
                    onChange={(e) => setReplyContent(e.target.value)}
                    placeholder="Type your response to the user here..."
                    className={`w-full p-4 border border-gray-200 dark:border-[#32353c] rounded-xl focus:outline-none focus:ring-2 focus:ring-[#174568]/10 dark:focus:ring-blue-500/10 focus:border-[#174568] dark:focus:border-blue-500 min-h-[150px] text-sm leading-relaxed transition-all ${
                      selectedQuote.status === 'replied' ? 'bg-gray-50 dark:bg-inherit dark:text-gray-400' : 'bg-white dark:bg-inherit dark:text-white'
                    }`}
                    required
                  ></textarea>

                  {successMessage && (
                    <div className="mt-4 p-3 bg-green-50 text-green-700 rounded-lg flex items-center gap-2 text-sm border border-green-100">
                      <CheckCircle size={16} /> {successMessage}
                    </div>
                  )}

                  <div className="mt-6 flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="px-6 py-2 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 font-medium transition-colors"
                    >
                      Close
                    </button>
                    {selectedQuote.status === 'open' && (
                      <button
                        type="submit"
                        disabled={isReplying || !replyContent.trim()}
                        className="bg-[#174568] text-white px-8 py-2 rounded-md text-[12px] md:text-base font-medium hover:bg-[#1a3a5a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                      >
                        {isReplying ? (
                          <>
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail size={16} />
                            Send Response
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminSidebarLayout>
  );
};

export default ProjectRequests;
